/**
 * API Tokens Management Service
 * Canonical implementation for API token operations
 */

import { buildTenantUrl } from "../../config/services-config";
import type { HttpClient } from "../client";
import type { ApiToken, CreateTokenRequest } from "../types";

export class TokensService {
	private httpClient: HttpClient;

	constructor(httpClient: HttpClient) {
		this.httpClient = httpClient;
	}

	// ==================== Token Listing ====================

	async getTokens(tenantSlug: string): Promise<ApiToken[]> {
		const url = buildTenantUrl(tenantSlug, "/admin/tokens");
		const response = await this.httpClient.get<{ tokens: ApiToken[] }>(url);
		return response.tokens;
	}

	async getToken(tenantSlug: string, tokenId: string): Promise<ApiToken> {
		const url = buildTenantUrl(tenantSlug, `/admin/tokens/${tokenId}`);
		return await this.httpClient.get<ApiToken>(url);
	}

	// ==================== Token Creation ====================

	async createToken(
		tenantSlug: string,
		request: CreateTokenRequest,
	): Promise<{
		token: ApiToken;
		secretToken: string;
	}> {
		const url = buildTenantUrl(tenantSlug, "/admin/tokens");
		return await this.httpClient.post<{
			token: ApiToken;
			secretToken: string;
		}>(url, request);
	}

	// ==================== Token Management ====================

	async updateToken(
		tenantSlug: string,
		tokenId: string,
		updates: {
			name?: string;
			description?: string;
			scopes?: string[];
		},
	): Promise<ApiToken> {
		const url = buildTenantUrl(tenantSlug, `/admin/tokens/${tokenId}`);
		return await this.httpClient.patch<ApiToken>(url, updates);
	}

	async deleteToken(tenantSlug: string, tokenId: string): Promise<void> {
		const url = buildTenantUrl(tenantSlug, `/admin/tokens/${tokenId}`);
		await this.httpClient.delete<void>(url);
	}

	async revokeToken(tenantSlug: string, tokenId: string): Promise<void> {
		const url = buildTenantUrl(tenantSlug, `/admin/tokens/${tokenId}/revoke`);
		await this.httpClient.post<void>(url);
	}

	// ==================== Token Usage ====================

	async getTokenUsage(
		tenantSlug: string,
		tokenId: string,
		timeframe: string = "7d",
	): Promise<{
		usage: Array<{
			date: string;
			requests: number;
			ecosystems: Record<string, number>;
		}>;
		total: {
			requests: number;
			ecosystems: Record<string, number>;
		};
	}> {
		const url = buildTenantUrl(
			tenantSlug,
			`/admin/tokens/${tokenId}/usage?timeframe=${timeframe}`,
		);
		return await this.httpClient.get<any>(url);
	}

	async getAllTokensUsage(
		tenantSlug: string,
		timeframe: string = "7d",
	): Promise<{
		tokens: Array<{
			id: string;
			name: string;
			requests: number;
			lastUsed?: string;
		}>;
		total: number;
	}> {
		const url = buildTenantUrl(
			tenantSlug,
			`/admin/tokens/usage?timeframe=${timeframe}`,
		);
		return await this.httpClient.get<any>(url);
	}

	// ==================== Token Validation ====================

	async validateToken(tokenValue: string): Promise<{
		valid: boolean;
		token?: {
			id: string;
			name: string;
			scopes: string[];
			tenantId: string;
			expiresAt?: string;
		};
		error?: string;
	}> {
		// Note: This would typically be called by the API gateway
		// For dashboard purposes, we might not need this
		const url = "/api/auth/validate-token";
		return await this.httpClient.post<any>(url, { token: tokenValue });
	}

	// ==================== Available Scopes ====================

	async getAvailableScopes(): Promise<{
		scopes: Array<{
			name: string;
			description: string;
			category: "packages" | "admin" | "analytics";
		}>;
	}> {
		// This might be a static configuration or from the tenant service
		const url = buildTenantUrl("", "/admin/scopes"); // No tenant needed for global scopes
		return await this.httpClient.get<any>(url);
	}

	// ==================== Token Templates ====================

	async getTokenTemplates(): Promise<{
		templates: Array<{
			name: string;
			description: string;
			scopes: string[];
			ecosystem?: string;
		}>;
	}> {
		const url = buildTenantUrl("", "/admin/token-templates");
		return await this.httpClient.get<any>(url);
	}

	async createTokenFromTemplate(
		tenantSlug: string,
		templateName: string,
		tokenName: string,
	): Promise<{
		token: ApiToken;
		secretToken: string;
	}> {
		const url = buildTenantUrl(tenantSlug, "/admin/tokens/from-template");
		return await this.httpClient.post<any>(url, {
			template: templateName,
			name: tokenName,
		});
	}
}
